import { useLazyQuery, useQuery } from '@apollo/client'
import {
  Button,
  Hidden,
  Typography,
  Collapse,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import FilterListIcon from '@material-ui/icons/FilterList'
import { Pagination } from '@material-ui/lab'
import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'

import PageHeader from '../../components/PageHeader'
import { WALLETS_QUERY } from '../../graphql/wallet'
import { WALLET_TYPES_QUERY } from '../../graphql/walletTypes'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import { ROLE } from '../../staticRoleManagement/types'
import { WalletFilters, WalletListItem, WalletType } from '../../types/wallet'
import { WalletSortBy } from '../../types/walletEnums'
import { FilterBoxDesktop } from './FilterBoxDesktop'
import { WalletListTableDesktop } from './WalletListTableDesktop'
import useStyles from './styles/index.styles'

export default function WalletList() {
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const perPage = 5

  const [page, setPage] = useState<number>(1)
  const [sort, setSort] = useState<WalletSortBy>('default')
  const isRoleAdmin = getRoleFromStorage() === ROLE.ADMIN
  const [filters, setfilters] = useState<WalletFilters>({
    sortBy: 'default',
    active: undefined,
    canUseCredit: undefined,
    currency: undefined,
    entity: undefined,
    walletTypes: undefined,
    creditorEntity: undefined
  })
  const [filterButtonSelected, setFilterButtonSelected] =
    useState<boolean>(false)
  const [count, setcount] = useState(0)

  const numberOfPages = useMemo(() => Math.ceil(count / perPage), [count])

  const {
    data: walletTypes,
    loading: loadingWalletTypes,
    error: walletTypesError,
    refetch: refetchWalletTypes
  } = useQuery<{
    walletTypes: WalletType[]
  }>(WALLET_TYPES_QUERY, {
    notifyOnNetworkStatusChange: true,
    onError: () => {
      toast.error(getTranslate('Unable to load wallet types. Please retry.'))
    }
  })

  const [getWallets, { data: wallets, loading: loadingForWallets, refetch }] =
    useLazyQuery<{ wallets: WalletListItem[] }>(WALLETS_QUERY, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        setcount(res.wallets.length)
      }
    })

  useEffect(() => {
    getWallets({
      variables: {
        filters: filters
      }
    })
  }, [sort])

  return (
    <div>
      <PageHeader title={getTranslate('Wallet List')} />
      <div className={classes.headerAndButtons}>
        <div className={classes.header}>
          <Typography variant="h3" gutterBottom>
            {getTranslate('Wallets')}
          </Typography>
        </div>
        {
          <div className={classes.buttons}>
            {isRoleAdmin && (
              <Button
                size={isSmallScreen ? 'large' : 'small'}
                startIcon={<AddIcon />}
                variant="contained"
                color="primary"
                component={LocalizedLink}
                to="/adopay/wallet/create">
                {isSmallScreen ? '' : getTranslate('New Wallet')}
              </Button>
            )}
            <Button
              size={isSmallScreen ? 'large' : 'small'}
              startIcon={<FilterListIcon />}
              variant="outlined"
              color="primary"
              onClick={() => setFilterButtonSelected(!filterButtonSelected)}>
              {isSmallScreen ? '' : getTranslate('Filter')}
            </Button>
          </div>
        }
      </div>
      <Hidden smDown>
        <Collapse in={filterButtonSelected}>
          <FilterBoxDesktop
            filters={filters}
            setfilters={setfilters}
            setPage={setPage}
            getWallets={getWallets}
            loadingForWalletTypes={loadingWalletTypes}
            loadingForWallets={loadingForWallets}
            walletTypes={walletTypes?.walletTypes || []}
            walletTypesError={Boolean(walletTypesError)}
            retryWalletTypes={() => {
              refetchWalletTypes()
            }}
            isRoleAdmin={isRoleAdmin}
          />
        </Collapse>
      </Hidden>
      <Hidden smDown>
        <WalletListTableDesktop
          data={
            wallets?.wallets.slice((page - 1) * perPage, page * perPage) || []
          }
          {...{
            filters,
            setfilters,
            refetch,
            setPage,
            sort,
            setSort,
            loadingForWallets
          }}
        />
      </Hidden>
      <div className={classes.paginationContainer}>
        <Pagination
          count={numberOfPages}
          color="primary"
          page={page}
          onChange={(_, value) => {
            setPage(value)
            getWallets({
              variables: {
                filters: filters
              }
            })
          }}
        />
      </div>
    </div>
  )
}
